import { useCallback, useMemo, type Dispatch, type SetStateAction } from 'react';
import { addDays, format, startOfDay } from 'date-fns';
import { doc, setDoc, updateDoc, writeBatch, deleteField } from 'firebase/firestore';
import type { CollectionReference, DocumentData, DocumentReference } from 'firebase/firestore';
import { toast } from '@/hooks/use-toast';
import { db } from '@/lib/firebase';
import { mapWithConcurrency } from '@/pages/developer/githubUtils';
import type { Commit, JournalEntry, TimeEntry } from '@/pages/developer/types';

const DEFAULT_HOURLY_RATE = 65;

type Setter<T> = Dispatch<SetStateAction<T>>;

type WorkorderParams = {
  commits: Commit[];
  timeEntries: TimeEntry[];
  journalEntries: JournalEntry[];
  workorderDialogEntryId: string | null;
  workorderDialogCommitShas: string[];
  workorderDialogRate: string;
  workorderDialogHours: string;
  workorderDialogHourlyRate: string;
  workorderDialogMisc: string;
  workorderDialogTaxes: string;
  timeDate: string;
  assignCommitDialogShas: string[];
  assignCommitDialogWorkorderId: string;
  assignCommitDialogNewWorkorderDescription: string;
  developerUnlocked: boolean;
  isAdminUser: boolean;
  ratePerCommit: number;
  firestoreCommitsCollectionRef: CollectionReference<DocumentData> | null;
  firestoreDocRef: DocumentReference<DocumentData> | null;
  openInvoiceEditor: (entryId: string) => void;
  closeGitOrder: (entryId: string) => Promise<void>;
  generateId: () => string;
  setDeveloperDialogOpen: Setter<boolean>;
  setAssignCommitDialogShas: Setter<string[]>;
  setAssignCommitDialogWorkorderId: Setter<string>;
  setAssignCommitDialogNewWorkorderDescription: Setter<string>;
  setAssignCommitDialogOpen: Setter<boolean>;
  setAssignCommitDialogSaving: Setter<boolean>;
  setAssignCommitDialogCreating: Setter<boolean>;
  setTimeEntries: Setter<TimeEntry[]>;
  setCommits: Setter<Commit[]>;
  setWorkorderDialogEntryId: Setter<string | null>;
  setWorkorderDialogCommitShas: Setter<string[]>;
  setWorkorderDialogRate: Setter<string>;
  setWorkorderDialogHours: Setter<string>;
  setWorkorderDialogHourlyRate: Setter<string>;
  setWorkorderDialogMisc: Setter<string>;
  setWorkorderDialogTaxes: Setter<string>;
  setWorkorderDialogOpen: Setter<boolean>;
  setWorkorderDialogSaving: Setter<boolean>;
};

type WorkorderReturn = {
  resolveTimeEntryKind: (entry: TimeEntry) => 'time-ticket' | 'git-order';
  timeTickets: TimeEntry[];
  gitOrders: TimeEntry[];
  selectedWorkorder: TimeEntry | null;
  commitsForSelectedWorkorder: Commit[];
  commitsForSelectedTimeDate: Commit[];
  assignableWorkorders: TimeEntry[];
  selectedAssignCommits: Commit[];
  primaryAssignCommit: Commit | null;
  openAssignCommitsToGitOrderDialog: (commitShas: string[]) => void;
  openAssignCommitToGitOrderDialog: (commitSha: string) => void;
  assignCommitsToGitOrder: () => Promise<void>;
  createGitOrderFromCommits: () => Promise<void>;
  openWorkorderCommitsDialog: (entryId: string) => void;
  saveWorkorderCommits: () => Promise<void>;
  openInvoiceForGitOrder: (gitOrderId: string) => void;
};

export const useDeveloperWorkorders = (params: WorkorderParams): WorkorderReturn => {
  const resolveTimeEntryKind = useCallback((entry: TimeEntry): 'time-ticket' | 'git-order' => {
    if (entry.kind === 'time-ticket') return 'time-ticket';
    if (entry.kind === 'git-order') return 'git-order';

    const hasCommitBillingData =
      (entry.commitShas?.length ?? 0) > 0 ||
      typeof entry.commitRate === 'number' ||
      typeof entry.misc === 'number' ||
      typeof entry.taxes === 'number' ||
      Boolean(entry.invoiceJournalEntryId);
    return hasCommitBillingData ? 'git-order' : 'time-ticket';
  }, []);

  const timeTickets = useMemo(() => {
    return params.timeEntries.filter((entry) => resolveTimeEntryKind(entry) === 'time-ticket');
  }, [params.timeEntries, resolveTimeEntryKind]);

  const gitOrders = useMemo(() => {
    return params.timeEntries.filter((entry) => resolveTimeEntryKind(entry) === 'git-order');
  }, [params.timeEntries, resolveTimeEntryKind]);

  const selectedWorkorder = useMemo(() => {
    if (!params.workorderDialogEntryId) return null;
    return gitOrders.find((entry) => entry.id === params.workorderDialogEntryId) ?? null;
  }, [gitOrders, params.workorderDialogEntryId]);

  const commitsForSelectedWorkorder = useMemo(() => {
    if (!selectedWorkorder) return [] as Commit[];
    const selectedShaSet = new Set((selectedWorkorder.commitShas ?? []).map((sha) => sha.trim()).filter(Boolean));

    return params.commits.filter((commit) => {
      const sha = (commit.sha ?? '').trim();
      if (!sha) return false;
      const commitWorkorderId = (commit.workorderId ?? '').trim();
      if (!commitWorkorderId) return true;
      if (commitWorkorderId === selectedWorkorder.id) return true;
      return selectedShaSet.has(sha);
    });
  }, [params.commits, selectedWorkorder]);

  const commitsForSelectedTimeDate = useMemo(() => {
    if (!params.timeDate) return [] as Commit[];
    const selectedDateStr = params.timeDate.trim();
    if (!selectedDateStr) return [];

    const selectedDate = startOfDay(new Date(selectedDateStr));
    const endOfSelectedDate = addDays(selectedDate, 1);

    return params.commits.filter((commit) => {
      const commitDateStr = commit.date ?? '';
      if (!commitDateStr) return false;
      const commitDate = new Date(commitDateStr);
      if (Number.isNaN(commitDate.getTime())) return false;
      const commitWorkorderId = (commit.workorderId ?? '').trim();
      if (commitWorkorderId) return false;
      return commitDate >= selectedDate && commitDate < endOfSelectedDate;
    });
  }, [params.commits, params.timeDate]);

  const assignableWorkorders = useMemo(() => {
    return gitOrders.filter((entry) => entry.stage !== 'invoiced' && !entry.invoiceJournalEntryId);
  }, [gitOrders]);

  const selectedAssignCommits = useMemo(() => {
    const shas = params.assignCommitDialogShas.map((sha) => sha.trim()).filter(Boolean);
    if (shas.length === 0) return [] as Commit[];
    const shaSet = new Set(shas);
    return params.commits.filter((commit) => shaSet.has(commit.sha));
  }, [params.assignCommitDialogShas, params.commits]);

  const primaryAssignCommit = useMemo(() => selectedAssignCommits[0] ?? null, [selectedAssignCommits]);

  const openAssignCommitsToGitOrderDialog = useCallback(
    (commitShas: string[]) => {
      if (!params.developerUnlocked && !params.isAdminUser) {
        toast({
          title: 'Developer unlock required',
          description: 'Enter the developer passcode to assign commits to Git orders.',
        });
        params.setDeveloperDialogOpen(true);
        return;
      }

      const resolved = Array.from(new Set(commitShas.map((sha) => sha.trim()).filter(Boolean)))
        .map((sha) => params.commits.find((candidate) => candidate.sha === sha) ?? null)
        .filter((value): value is Commit => Boolean(value));

      if (resolved.length === 0) {
        toast({ title: 'No commits selected', description: 'There are no commits to assign.' });
        return;
      }

      const primary = resolved[0];

      const existingWorkorderId = (primary.workorderId ?? '').trim();
      const committedAt = new Date(primary.date);
      const commitDayKey = Number.isNaN(committedAt.getTime()) ? '' : format(committedAt, 'yyyy-MM-dd');
      const suggested = assignableWorkorders.find((entry) => {
        const entryDate = new Date(entry.date);
        if (Number.isNaN(entryDate.getTime())) return false;
        return format(entryDate, 'yyyy-MM-dd') === commitDayKey;
      });

      const existingAssignable = existingWorkorderId
        ? assignableWorkorders.find((entry) => entry.id === existingWorkorderId) ?? null
        : null;

      params.setAssignCommitDialogShas(resolved.map((commit) => commit.sha));
      params.setAssignCommitDialogWorkorderId(existingAssignable?.id ?? suggested?.id ?? '');
      params.setAssignCommitDialogNewWorkorderDescription('');
      params.setAssignCommitDialogOpen(true);
    },
    [
      assignableWorkorders,
      params,
    ]
  );

  const openAssignCommitToGitOrderDialog = useCallback(
    (commitSha: string) => openAssignCommitsToGitOrderDialog([commitSha]),
    [openAssignCommitsToGitOrderDialog]
  );

  const assignCommitsToGitOrder = useCallback(async () => {
    if (!params.developerUnlocked && !params.isAdminUser) return;
    const targetId = params.assignCommitDialogWorkorderId.trim();
    if (selectedAssignCommits.length === 0 || !targetId) return;

    const target = assignableWorkorders.find((entry) => entry.id === targetId) ?? null;
    if (!target) {
      toast({ title: 'Git order not found', description: 'That Git order is no longer available.' });
      return;
    }

    const assignableIds = new Set(assignableWorkorders.map((entry) => entry.id));
    const lockedCommits = selectedAssignCommits.filter((commit) => {
      const existingId = (commit.workorderId ?? '').trim();
      return Boolean(existingId) && !assignableIds.has(existingId);
    });
    if (lockedCommits.length > 0) {
      toast({
        title: 'Git order locked',
        description:
          lockedCommits.length === 1
            ? 'One selected commit is assigned to a closed/locked Git order and cannot be moved.'
            : `${lockedCommits.length} selected commits are assigned to closed/locked Git orders and cannot be moved.`,
      });
      return;
    }

    params.setAssignCommitDialogSaving(true);
    try {
      const idsToUpdate = new Set<string>([target.id]);
      const updatedCommitSets = new Map<string, Set<string>>();

      const getEntrySet = (entryId: string) => {
        const existing = updatedCommitSets.get(entryId);
        if (existing) return existing;
        const entry = params.timeEntries.find((candidate) => candidate.id === entryId) ?? null;
        const next = new Set((entry?.commitShas ?? []).map((sha) => sha.trim()).filter(Boolean));
        updatedCommitSets.set(entryId, next);
        return next;
      };

      selectedAssignCommits.forEach((commit) => {
        const existingId = (commit.workorderId ?? '').trim();
        if (existingId && existingId !== target.id) {
          idsToUpdate.add(existingId);
          getEntrySet(existingId).delete(commit.sha);
        }
        getEntrySet(target.id).add(commit.sha);
      });

      const nextTimeEntries = params.timeEntries.map((entry) => {
        if (!idsToUpdate.has(entry.id)) return entry;
        const nextSet = updatedCommitSets.get(entry.id) ?? new Set((entry.commitShas ?? []).map((sha) => sha.trim()).filter(Boolean));
        const nextCommitShas = Array.from(nextSet);
        const nextRate = typeof entry.commitRate === 'number' ? entry.commitRate : params.ratePerCommit;
        return {
          ...entry,
          commitShas: nextCommitShas,
          commitRate: nextCommitShas.length > 0 ? nextRate : entry.commitRate,
        };
      });

      params.setTimeEntries(nextTimeEntries);
      params.setCommits((prev) =>
        prev.map((candidate) =>
          selectedAssignCommits.some((selected) => selected.sha === candidate.sha)
            ? {
                ...candidate,
                workStatus: 'assigned',
                workorderId: target.id,
              }
            : candidate
        )
      );

      if (db && params.firestoreCommitsCollectionRef) {
        const results = await mapWithConcurrency(
          selectedAssignCommits,
          async (commit) => {
            try {
              await updateDoc(doc(params.firestoreCommitsCollectionRef, commit.sha), {
                workStatus: 'assigned',
                workorderId: target.id,
              });
              return { sha: commit.sha, ok: true } as const;
            } catch (error) {
              const message = error instanceof Error ? error.message : 'Unable to update commit status.';
              return { sha: commit.sha, ok: false, message } as const;
            }
          },
          { concurrency: 6 }
        );

        const failures = results.filter((result) => !result.ok);
        if (failures.length > 0) {
          toast({
            title: 'Some commits failed to update',
            description: failures.length === 1 ? failures[0].message : `${failures.length} commits could not be updated in Firestore.`,
          });
        }
      }

      if (params.firestoreDocRef) {
        try {
          await updateDoc(params.firestoreDocRef, { timeEntries: nextTimeEntries });
        } catch {
          await setDoc(
            params.firestoreDocRef,
            {
              timeEntries: nextTimeEntries,
            },
            { merge: true }
          );
        }
      }

      toast({
        title: 'Saved',
        description:
          selectedAssignCommits.length === 1
            ? 'Commit assigned to Git order.'
            : `Assigned ${selectedAssignCommits.length} commits to Git order.`,
      });
      params.setAssignCommitDialogOpen(false);
    } finally {
      params.setAssignCommitDialogSaving(false);
    }
  }, [assignableWorkorders, params, selectedAssignCommits]);

  const createGitOrderFromCommits = useCallback(async () => {
    if (!params.developerUnlocked && !params.isAdminUser) return;
    const description = params.assignCommitDialogNewWorkorderDescription.trim();
    if (!description || selectedAssignCommits.length === 0) return;

    const primary = primaryAssignCommit;
    if (!primary) return;

    const when = new Date(primary.date);
    const dateKey = Number.isNaN(when.getTime()) ? null : format(when, 'yyyy-MM-dd');
    if (!dateKey) {
      toast({ title: 'Invalid commit date', description: 'This commit has an invalid date.' });
      return;
    }

    const assignableIds = new Set(assignableWorkorders.map((entry) => entry.id));
    const lockedCommits = selectedAssignCommits.filter((commit) => {
      const existingId = (commit.workorderId ?? '').trim();
      return Boolean(existingId) && !assignableIds.has(existingId);
    });
    if (lockedCommits.length > 0) {
      toast({
        title: 'Git order locked',
        description:
          lockedCommits.length === 1
            ? 'One selected commit is assigned to a closed/locked Git order and cannot be moved.'
            : `${lockedCommits.length} selected commits are assigned to closed/locked Git orders and cannot be moved.`,
      });
      return;
    }

    const entry: TimeEntry = {
      id: params.generateId(),
      date: new Date(dateKey).toISOString(),
      hours: 0,
      hourlyRate: DEFAULT_HOURLY_RATE,
      description,
      billable: true,
      kind: 'git-order',
      stage: 'workorder',
      commitShas: selectedAssignCommits.map((commit) => commit.sha),
      commitRate: params.ratePerCommit,
      invoiceJournalEntryId: null,
    };

    params.setAssignCommitDialogCreating(true);
    try {
      const nextTimeEntries = [entry, ...params.timeEntries];

      const commitShaSet = new Set(entry.commitShas ?? []);
      const finalTimeEntries = nextTimeEntries.map((candidate) => {
        if (candidate.id === entry.id) return candidate;
        const current = (candidate.commitShas ?? []).map((sha) => sha.trim()).filter(Boolean);
        if (current.length === 0) return candidate;
        const next = current.filter((sha) => !commitShaSet.has(sha));
        if (next.length === current.length) return candidate;
        return { ...candidate, commitShas: next };
      });

      params.setTimeEntries(finalTimeEntries);
      params.setCommits((prev) =>
        prev.map((candidate) =>
          selectedAssignCommits.some((selected) => selected.sha === candidate.sha)
            ? {
                ...candidate,
                workStatus: 'assigned',
                workorderId: entry.id,
              }
            : candidate
        )
      );

      if (params.firestoreDocRef) {
        try {
          await updateDoc(params.firestoreDocRef, { timeEntries: finalTimeEntries });
        } catch {
          await setDoc(params.firestoreDocRef, { timeEntries: finalTimeEntries }, { merge: true });
        }
      } else {
        toast({ title: 'Saved locally', description: 'Connect Firestore to sync Git orders.' });
      }

      if (db && params.firestoreCommitsCollectionRef) {
        const results = await mapWithConcurrency(
          selectedAssignCommits,
          async (commit) => {
            try {
              await updateDoc(doc(params.firestoreCommitsCollectionRef, commit.sha), {
                workStatus: 'assigned',
                workorderId: entry.id,
              });
              return { sha: commit.sha, ok: true } as const;
            } catch (error) {
              const message = error instanceof Error ? error.message : 'Unable to update commit status.';
              return { sha: commit.sha, ok: false, message } as const;
            }
          },
          { concurrency: 6 }
        );

        const failures = results.filter((result) => !result.ok);
        if (failures.length > 0) {
          toast({
            title: 'Some commits failed to update',
            description: failures.length === 1 ? failures[0].message : `${failures.length} commits could not be updated in Firestore.`,
          });
        }
      }

      params.setAssignCommitDialogWorkorderId(entry.id);
      params.setAssignCommitDialogNewWorkorderDescription('');
      toast({
        title: 'Git order created',
        description: selectedAssignCommits.length === 1 ? 'Created a new Git order and assigned the commit.' : 'Created a new Git order and assigned the commits.',
      });
    } finally {
      params.setAssignCommitDialogCreating(false);
    }
  }, [assignableWorkorders, params, primaryAssignCommit, selectedAssignCommits]);

  const openWorkorderCommitsDialog = useCallback(
    (entryId: string) => {
      const entry = gitOrders.find((candidate) => candidate.id === entryId) ?? null;
      if (!entry) {
        toast({ title: 'Git order not found', description: 'That Git order could not be found.' });
        return;
      }

      const selected = (entry.commitShas ?? []).map((sha) => sha.trim()).filter(Boolean);
      const initialRate = typeof entry.commitRate === 'number' ? entry.commitRate : params.ratePerCommit;
      const initialHourlyRate = typeof entry.hourlyRate === 'number' ? entry.hourlyRate : DEFAULT_HOURLY_RATE;
      params.setWorkorderDialogEntryId(entryId);
      params.setWorkorderDialogCommitShas(selected);
      params.setWorkorderDialogRate(String(initialRate));
      params.setWorkorderDialogHours(String(typeof entry.hours === 'number' ? entry.hours : 0));
      params.setWorkorderDialogHourlyRate(String(initialHourlyRate));
      params.setWorkorderDialogMisc(typeof entry.misc === 'number' ? String(entry.misc) : '');
      params.setWorkorderDialogTaxes(typeof entry.taxes === 'number' ? String(entry.taxes) : '');
      params.setWorkorderDialogOpen(true);
    },
    [gitOrders, params]
  );

  const saveWorkorderCommits = useCallback(async () => {
    const entry = selectedWorkorder;
    if (!entry) return;

    const rateNumber = params.workorderDialogRate.trim() ? Number(params.workorderDialogRate) : params.ratePerCommit;
    const hoursNumber = params.workorderDialogHours.trim() ? Number(params.workorderDialogHours) : 0;
    const hourlyRateNumber = params.workorderDialogHourlyRate.trim() ? Number(params.workorderDialogHourlyRate) : DEFAULT_HOURLY_RATE;
    const miscNumber = params.workorderDialogMisc.trim() ? Number(params.workorderDialogMisc) : 0;
    const taxesNumber = params.workorderDialogTaxes.trim() ? Number(params.workorderDialogTaxes) : 0;
    if (!Number.isFinite(rateNumber) || rateNumber < 0) {
      toast({ title: 'Invalid rate', description: 'Rate must be a valid number.' });
      return;
    }
    if (!Number.isFinite(hoursNumber) || hoursNumber < 0) {
      toast({ title: 'Invalid hours', description: 'Hours must be a valid number.' });
      return;
    }
    if (!Number.isFinite(hourlyRateNumber) || hourlyRateNumber < 0) {
      toast({ title: 'Invalid hourly rate', description: 'Hourly rate must be a valid number.' });
      return;
    }
    if ((params.workorderDialogMisc.trim() && !Number.isFinite(miscNumber)) || (params.workorderDialogTaxes.trim() && !Number.isFinite(taxesNumber))) {
      toast({ title: 'Invalid totals', description: 'Misc and Taxes must be valid numbers.' });
      return;
    }

    const selectedCommitShas = params.workorderDialogCommitShas.map((sha) => sha.trim()).filter(Boolean);
    const selectedSet = new Set(selectedCommitShas);
    const previousCommitShas = (entry.commitShas ?? []).map((sha) => sha.trim()).filter(Boolean);
    const previousSet = new Set(previousCommitShas);

    const toAssign = selectedCommitShas.filter((sha) => !previousSet.has(sha));
    const toUnassign = previousCommitShas.filter((sha) => !selectedSet.has(sha));

    params.setWorkorderDialogSaving(true);
    try {
      const nextTimeEntries = params.timeEntries.map((candidate) =>
        candidate.id === entry.id
          ? {
              ...candidate,
              commitShas: selectedCommitShas,
              commitRate: selectedCommitShas.length > 0 ? rateNumber : candidate.commitRate,
              hours: hoursNumber,
              hourlyRate: hourlyRateNumber,
              misc: miscNumber ? miscNumber : undefined,
              taxes: taxesNumber ? taxesNumber : undefined,
            }
          : candidate
      );

      params.setTimeEntries(nextTimeEntries);

      if (toAssign.length > 0 || toUnassign.length > 0) {
        params.setCommits((prev) =>
          prev.map((commit) => {
            if (toAssign.includes(commit.sha)) {
              return {
                ...commit,
                workStatus: 'assigned',
                workorderId: entry.id,
              };
            }
            if (toUnassign.includes(commit.sha) && (commit.workorderId ?? '').trim() === entry.id) {
              return {
                ...commit,
                workStatus: 'unassigned',
                workorderId: undefined,
              };
            }
            return commit;
          })
        );
      }

      if (db && params.firestoreCommitsCollectionRef && (toAssign.length > 0 || toUnassign.length > 0)) {
        try {
          let batch = writeBatch(db);
          let ops = 0;
          for (const sha of toAssign) {
            batch.update(doc(params.firestoreCommitsCollectionRef, sha), {
              workStatus: 'assigned',
              workorderId: entry.id,
            });
            ops += 1;
            if (ops >= 450) {
              await batch.commit();
              batch = writeBatch(db);
              ops = 0;
            }
          }

          for (const sha of toUnassign) {
            batch.update(doc(params.firestoreCommitsCollectionRef, sha), {
              workStatus: 'unassigned',
              workorderId: deleteField(),
            });
            ops += 1;
            if (ops >= 450) {
              await batch.commit();
              batch = writeBatch(db);
              ops = 0;
            }
          }

          if (ops > 0) await batch.commit();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unable to update commit status.';
          toast({ title: 'Commit update failed', description: message });
        }
      }

      if (params.firestoreDocRef) {
        try {
          await updateDoc(params.firestoreDocRef, { timeEntries: nextTimeEntries });
        } catch {
          await setDoc(
            params.firestoreDocRef,
            {
              timeEntries: nextTimeEntries,
            },
            { merge: true }
          );
        }
      }

      toast({ title: 'Git order updated', description: 'Commit assignments saved.' });
      params.setWorkorderDialogOpen(false);
    } finally {
      params.setWorkorderDialogSaving(false);
    }
  }, [params, selectedWorkorder]);

  const openInvoiceForGitOrder = useCallback(
    (gitOrderId: string) => {
      const entry = params.timeEntries.find((candidate) => candidate.id === gitOrderId) ?? null;
      if (!entry) {
        toast({ title: 'Git order not found', description: 'That Git order could not be found.' });
        return;
      }
      if (resolveTimeEntryKind(entry) !== 'git-order') {
        toast({ title: 'Not a Git order', description: 'Only Git orders can be linked to invoices.' });
        return;
      }

      const invoiceId = (entry.invoiceJournalEntryId ?? '').trim();
      const invoiceExists = invoiceId ? params.journalEntries.some((candidate) => candidate.id === invoiceId) : false;
      if (invoiceId && invoiceExists) {
        params.openInvoiceEditor(invoiceId);
        return;
      }

      void params.closeGitOrder(entry.id);
    },
    [params, resolveTimeEntryKind]
  );

  return {
    resolveTimeEntryKind,
    timeTickets,
    gitOrders,
    selectedWorkorder,
    commitsForSelectedWorkorder,
    commitsForSelectedTimeDate,
    assignableWorkorders,
    selectedAssignCommits,
    primaryAssignCommit,
    openAssignCommitsToGitOrderDialog,
    openAssignCommitToGitOrderDialog,
    assignCommitsToGitOrder,
    createGitOrderFromCommits,
    openWorkorderCommitsDialog,
    saveWorkorderCommits,
    openInvoiceForGitOrder,
  };
};
